import { useMutation } from '@apollo/client';
import { Button, Pill, Stack, Text, useNotifications } from '@sixfold/common-ui';
import React, { useState } from 'react';

import {
  TestOvdTokenFilterMutation,
  TestOvdTokenFilterMutationVariables,
  TestOvdTokenFilterInput,
} from '../../lib/graphql';
import { testOvdTokenFilterMutation } from '../graphql';

export function useOvdTokenTestFilter() {
  const notify = useNotifications();
  const [testFilter, { loading }] = useMutation<TestOvdTokenFilterMutation, TestOvdTokenFilterMutationVariables>(
    testOvdTokenFilterMutation,
  );
  const [result, setResult] = useState<TestOvdTokenFilterMutation['testOvdTokenFilter'] | null>(null);

  const handleTest = async (input: TestOvdTokenFilterInput) => {
    let res;
    try {
      res = await testFilter({
        variables: { input },
      });
    } catch (e) {
      console.error(e);
      notify.error({
        title: 'Failed to test filter',
        message:
          e instanceof Error && e.message.includes('500')
            ? 'The server encountered an internal error. Please check the token filter settings, try again later, or contact support if the problem persists.'
            : e instanceof Error
              ? e.message
              : undefined,
      });
      setResult(null);
      return;
    }
    setResult(res.data?.testOvdTokenFilter ?? null);
  };

  return { handleTest, loading, result };
}

// TODO: Hidden until the test filter mutation is fixed (returns incorrect results for multi-platform tokens)
const SHOW_TEST_FILTER = false;

export const OvdTokenTestFilterActions: React.FC<{
  onTest: () => void;
  loading: boolean;
  matchingShipmentCount: number | null;
}> = ({ onTest, loading, matchingShipmentCount }) =>
  SHOW_TEST_FILTER ? (
    <>
      <Button type="button" kind="subtle" onClick={onTest} disabled={loading}>
        {loading ? 'Testing...' : 'Test token'}
      </Button>
      {matchingShipmentCount !== null && (
        <Text>
          <strong>Matching shipments:</strong> {matchingShipmentCount}
        </Text>
      )}
    </>
  ) : null;

export const OvdTokenTestFilterResults: React.FC<{
  result: TestOvdTokenFilterMutation['testOvdTokenFilter'] | null;
}> = ({ result }) => {
  if (!SHOW_TEST_FILTER || result === null || result.sampleShipments.length === 0) {
    return null;
  }

  return (
    <Stack direction="column" gap="xsmall">
      <Text weight="bold">Sample shipments:</Text>
      {result.sampleShipments.map((s) => (
        <Stack key={s.shipmentId} direction="row" gap="xsmall" alignItems="center">
          <Pill>{s.shipmentId.substring(0, 12)}...</Pill>
          {s.shipperTransportNumber && <Text>Shipper: {s.shipperTransportNumber}</Text>}
          {s.platformTransportNumber && <Text>Platform: {s.platformTransportNumber}</Text>}
        </Stack>
      ))}
    </Stack>
  );
};

export function buildTestFilterInput(fields: {
  platformIds: string[];
  shipperIds: string[];
  carrierIds: string[];
  deliveryCustomFields: string[];
  tourCustomFields: string[];
}): TestOvdTokenFilterInput {
  return {
    platformIds: fields.platformIds,
    ...(fields.shipperIds.length > 0 ? { shipperIds: fields.shipperIds } : {}),
    ...(fields.carrierIds.length > 0 ? { carrierIds: fields.carrierIds } : {}),
    ...(fields.deliveryCustomFields.length > 0 ? { deliveryCustomFields: fields.deliveryCustomFields } : {}),
    ...(fields.tourCustomFields.length > 0 ? { tourCustomFields: fields.tourCustomFields } : {}),
  };
}
